import axios from 'axios';

const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor per gestire errori auth
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password, totp_code = null, recaptcha_token = null) =>
  api.post('/auth/login', { username, password, totp_code, recaptcha_token });

export const getMe = () => api.get('/auth/me');

export const setup2FA = () => api.post('/auth/setup-2fa');

export const enable2FA = (code) => api.post('/auth/enable-2fa', { code });

export const disable2FA = (code) => api.post('/auth/disable-2fa', { code });

export const changePassword = (old_password, new_password) =>
  api.post('/auth/change-password', { old_password, new_password });

// Admin
export const getUsers = () => api.get('/admin/users');

export const createUser = (data) => api.post('/admin/users', data);

export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);

export const deleteUser = (id) => api.delete(`/admin/users/${id}`);

export const resetUserPassword = (id, new_password) =>
  api.post(`/admin/users/${id}/reset-password`, { new_password });

export const resetUser2FA = (id) => api.post(`/admin/users/${id}/reset-2fa`);

// Estrazioni
export const getRuote = () => api.get('/estrazioni/ruote');

export const getEstrazioni = (params = {}) => api.get('/estrazioni/', { params });

export const getUltimaEstrazione = () => api.get('/estrazioni/ultima');

export const getEstrazioniPerData = (data) => api.get(`/estrazioni/per-data/${data}`);

export const getEstrazioniPerNumero = (numero, ruota_id = null, limit = 50) =>
  api.get(`/estrazioni/numero/${numero}`, { params: { ruota_id, limit } });

// Statistiche
export const getRitardi = (ruota_id = null, limit = 20) =>
  api.get('/statistiche/ritardi', { params: { ruota_id, limit } });

export const getFrequenze = (ruota_id = null, limit = 20) =>
  api.get('/statistiche/frequenze', { params: { ruota_id, limit } });

export const getStatisticheNumero = (numero, ruota_id = null) =>
  api.get(`/statistiche/numero/${numero}`, { params: { ruota_id } });

export const getRiepilogo = (ruota_id = null) =>
  api.get('/statistiche/riepilogo', { params: { ruota_id } });

export const getCoppieFrequenti = (ruota_id = null, limit = 20) =>
  api.get('/statistiche/coppie-frequenti', { params: { ruota_id, limit } });

export const getTerzineFrequenti = (ruota_id = null, limit = 20) =>
  api.get('/statistiche/terzine-frequenti', { params: { ruota_id, limit } });

// Ipotesi
export const getIpotesi = (solo_non_convertite = false) =>
  api.get('/ipotesi/', { params: { solo_non_convertite } });

export const createIpotesi = (data) => api.post('/ipotesi/', data);

export const getIpotesiById = (id) => api.get(`/ipotesi/${id}`);

export const rivalutaIpotesi = (id) => api.put(`/ipotesi/${id}/rivaluta`);

export const convertiIpotesi = (id, data) => api.post(`/ipotesi/${id}/converti`, data);

export const deleteIpotesi = (id) => api.delete(`/ipotesi/${id}`);

export const valutaNumeri = (numeri, ruote = null) =>
  api.post('/ipotesi/valuta', { numeri, ruote });

// Giocate
export const getGiocate = (stato = null) =>
  api.get('/giocate/', { params: { stato } });

export const createGiocata = (data) => api.post('/giocate/', data);

export const getGiocataById = (id) => api.get(`/giocate/${id}`);

export const deleteGiocata = (id) => api.delete(`/giocate/${id}`);

export const getDashboard = () => api.get('/giocate/dashboard');

// Calcolo costo con importi separati
export const calcolaCosto = (numeri, ruote, importo_ambo = 0, importo_terno = 0, importo_quaterna = 0, importo_cinquina = 0) =>
  api.get('/giocate/calcola-costo', { 
    params: { numeri, ruote, importo_ambo, importo_terno, importo_quaterna, importo_cinquina } 
  });

export const getEstrazioniDisponibili = (limit = 30) =>
  api.get('/giocate/estrazioni-disponibili', { params: { limit } });

// Collector (Admin)
export const importText = (text) => api.post('/collector/import-text', { text });

export const aggiornaStatistiche = () => api.post('/collector/aggiorna-statistiche');

export const getCollectorStats = () => api.get('/collector/stats');

// Telegram
export const getTelegramInstructions = () => api.get('/telegram/get-chat-id');

export const registerTelegramChatId = (chat_id) => 
  api.post('/telegram/register', { chat_id });

export const testTelegram = () => api.post('/telegram/test');

// Generatore
export const generaSistema = (data) => api.post('/generatore/sistema', data);

export const generaSistemaRuota = (data) => api.post('/generatore/sistema-ruota', data);

export const giocataVeloce = () => api.get('/generatore/giocata-veloce');

export const getNumeriCaldi = (ruota_id = null, limit = 20) =>
  api.get('/generatore/numeri-caldi', { params: { ruota_id, limit } });

export const calcolaCostoSistema = (n_numeri, n_ruote, tipo = 'amboterno') =>
  api.get('/generatore/calcola-costo', { params: { n_numeri, n_ruote, tipo } });

export const salvaSistema = (data) => api.post('/generatore/salva-sistema', data);

// PDF Export
export const downloadPdfGiocate = () => 
  api.get('/pdf/giocate', { responseType: 'blob' });

export const downloadPdfGiocata = (id) => 
  api.get(`/pdf/giocata/${id}`, { responseType: 'blob' });

export const downloadPdfStatistiche = () => 
  api.get('/pdf/statistiche', { responseType: 'blob' });

// Vincite
export const verificaGiocata = (id) => api.post(`/vincite/verifica/${id}`);

export const verificaTutteGiocate = () => api.post('/vincite/verifica-tutte');

export const getDettaglioGiocata = (id) => api.get(`/vincite/dettaglio/${id}`);

export const verificaUltimaEstrazione = (id) => api.post(`/vincite/verifica-ultima/${id}`);

export const verificaSuData = (id, data) => api.post(`/vincite/verifica-data/${id}?data_estrazione=${data}`);

// Alert Ritardi
export const getAlert = () => api.get('/alert/');

export const createAlert = (data) => api.post('/alert/', data);

export const updateAlert = (id, data) => api.put(`/alert/${id}`, data);

export const deleteAlert = (id) => api.delete(`/alert/${id}`);

export const toggleAlert = (id) => api.post(`/alert/${id}/toggle`);

export const getNotificheLog = (limit = 50) => api.get('/alert/notifiche', { params: { limit } });

export const getNumeriSoprasoglia = () => api.get('/alert/soprasoglia');

export const testAlert = () => api.post('/alert/test');

// Sistemi Ridotti
export const getSistemiDisponibili = () => api.get('/sistemi-ridotti/disponibili');

export const generaSistemaRidotto = (data) => api.post('/sistemi-ridotti/genera', data);

export const calcolaCostoSistemaRidotto = (n_numeri, garanzia, n_ruote = 1, importo = 1) =>
  api.get('/sistemi-ridotti/calcola-costo', { 
    params: { n_numeri, garanzia, n_ruote, importo_per_colonna: importo } 
  });

export const giocaSistemaRidotto = (data) => api.post('/sistemi-ridotti/gioca', data);

export const getSistemiSalvati = () => api.get('/sistemi-ridotti/salvati');

export const getSistemaDettaglio = (id) => api.get(`/sistemi-ridotti/salvati/${id}`);

export const deleteSistemaSalvato = (id) => api.delete(`/sistemi-ridotti/salvati/${id}`);

export const verificaSistemaSalvato = (id) => api.post(`/sistemi-ridotti/salvati/${id}/verifica`);

// Confronto Ruote
export const analizzaNumeriPerRuota = (data) => api.post('/confronto-ruote/analizza', data);

export const getStoricoNumeriRuota = (numeri, ruotaId, limite = 50) => 
  api.get('/confronto-ruote/storico-numeri', { params: { numeri, ruota_id: ruotaId, limite } });

export const getCoppieFrequentiConfronto = (ruotaId = null, limite = 20) => 
  api.get('/confronto-ruote/coppie-frequenti', { params: { ruota_id: ruotaId, limite } });

export default api;
